import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Factory, Monitor, GraduationCap, Building2, Hospital, Truck, ArrowRight } from 'lucide-react';

const industries = [
  {
    id: 1,
    slug: 'manufacturing',
    icon: Factory,
    name: 'Manufacturing',
    description: 'Quality management and process optimization for manufacturing excellence',
    image: 'https://images.unsplash.com/photo-1754372069872-3fef8a815c87?w=800&q=80'
  },
  {
    id: 2,
    slug: 'information-technology',
    icon: Monitor,
    name: 'Information Technology',
    description: 'IT service management and software quality assurance training',
    image: 'https://images.unsplash.com/flagged/photo-1579274216947-86eaa4b00475?w=800&q=80'
  },
  {
    id: 3,
    slug: 'educational-institutions',
    icon: GraduationCap,
    name: 'Educational Institutions',
    description: 'Faculty development and educational quality management systems',
    image: 'https://images.unsplash.com/photo-1541829070764-84a7d30dd3f3?w=800&q=80'
  },
  {
    id: 4,
    slug: 'healthcare',
    icon: Hospital,
    name: 'Healthcare',
    description: 'Medical device quality and healthcare service management',
    image: 'https://images.pexels.com/photos/40568/medical-appointment-doctor-healthcare-40568.jpeg?w=800&q=80'
  },
  {
    id: 5,
    slug: 'corporate-services',
    icon: Building2,
    name: 'Corporate Services',
    description: 'Management systems and corporate compliance training',
    image: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=800&q=80'
  },
  {
    id: 6,
    slug: 'supply-chain-logistics',
    icon: Truck,
    name: 'Supply Chain & Logistics',
    description: 'Supply chain quality and logistics management excellence',
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80'
  }
];

export const IndustriesServed = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
    }
  };

  return (
    <section ref={ref} className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 space-y-4"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
            Industries <span className="text-blue-600">We Serve</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Delivering specialized training solutions across diverse sectors to meet unique industry requirements
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {industries.map((industry) => {
            const Icon = industry.icon;
            return (
              <motion.div
                key={industry.id}
                variants={itemVariants}
                className="group relative h-80 rounded-2xl hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden"
                onClick={() => navigate(`/industry-training/${industry.slug}`)}
              >
                {/* Background Image */}
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                  style={{ backgroundImage: `url(${industry.image})` }}
                />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/70 to-transparent opacity-90 group-hover:opacity-95 transition-opacity duration-300" />

                {/* Content */}
                <div className="relative h-full p-8 flex flex-col justify-end text-white z-10">
                  <motion.div
                    className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-all duration-300"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <Icon className="h-8 w-8 text-white" />
                  </motion.div>
                  <h3 className="text-2xl font-bold mb-3 group-hover:text-blue-400 transition-colors">
                    {industry.name}
                  </h3>
                  <p className="text-gray-200 leading-relaxed mb-4">
                    {industry.description}
                  </p>
                  <div className="flex items-center text-sm font-semibold text-blue-300 group-hover:text-blue-200">
                    View Training Programs
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
